/**
 * OrderDetailModal Component
 * Displays full order details including items, transaction history,
 * and shipping address in a modal view.
 */

import { useState, useEffect, memo } from 'react';
import {
  TruckIcon,
  MapPinIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { OathModal, OathDualSpinner, OathButton, OathAlert } from '../../shared/OathUI';
import { getOrderDetails } from '../../../services/oathFormsService';
import { StatusPill } from './formsConstants';

/**
 * OrderDetailModal
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Close handler
 * @param {number} orderId - Order ID to load details for
 */
const OrderDetailModal = ({ isOpen, onClose, orderId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !orderId) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const loadDetails = async () => {
      setLoading(true);
      setError(null);

      const result = await getOrderDetails(orderId);

      if (cancelled) return;

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to load order details');
      }
      setLoading(false);
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [isOpen, orderId]);

  const order = data?.order || data?.detailOrder;
  const items = data?.items || data?.stateFormList || [];
  const transactions = data?.transactions || data?.transactionList || [];
  const address = data?.shippingAddress || data?.agentAddress;

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Order #${orderId || ''}`}
      size="lg"
      footer={
        <OathButton variant="outline" onClick={onClose} className="ml-auto">
          Close
        </OathButton>
      }
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <OathDualSpinner size="md" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading order details...</p>
        </div>
      ) : error ? (
        <OathAlert variant="error" message={error} />
      ) : data ? (
        <div className="space-y-6">
          {/* Order Summary */}
          {order && (
            <div className="flex flex-wrap items-center gap-3">
              <StatusPill status={order.status} />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Placed {order.date}{order.time ? ` at ${order.time}` : ''}
              </span>
            </div>
          )}

          {/* Shipping Address */}
          {address && (
            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <MapPinIcon className="h-4 w-4 text-primary dark:text-primary-light" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Shipping Address
                </h4>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300 pl-6">
                <p className="font-medium">{address.firstName} {address.lastName}</p>
                <p>{address.addressLine1}</p>
                {address.addressLine2 && address.addressLine2 !== '----------' && (
                  <p>{address.addressLine2}</p>
                )}
                <p>{address.city}, {address.state} {address.zipCode}</p>
              </div>
            </div>
          )}

          {/* Order Items */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <TruckIcon className="h-4 w-4 text-primary dark:text-primary-light" />
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Items ({items.length})
              </h4>
            </div>
            {items.length > 0 ? (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50">
                      <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-2">
                        Form
                      </th>
                      <th className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-2 w-16">
                        Qty
                      </th>
                      <th className="text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-2 w-24">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                    {items.map((item, idx) => (
                      <tr key={item.orderItemId || idx}>
                        <td className="px-4 py-2.5 text-sm text-gray-900 dark:text-white">
                          {item.formName}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-300 text-center">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <StatusPill status={item.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">No items found</p>
            )}
          </div>

          {/* Transaction History */}
          {transactions.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ClockIcon className="h-4 w-4 text-primary dark:text-primary-light" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Activity
                </h4>
              </div>
              <div className="relative pl-6 space-y-0">
                {/* Timeline line */}
                <div className="absolute left-[9px] top-1 bottom-1 w-px bg-gray-200 dark:bg-gray-700" />

                {transactions.map((tx, idx) => (
                  <div key={tx.transactionId || idx} className="relative pb-4 last:pb-0">
                    {/* Timeline dot */}
                    <div className="absolute -left-[15px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-gray-800 bg-primary" />

                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                      <StatusPill status={tx.status} />
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {tx.date}{tx.time ? ` ${tx.time}` : ''}
                      </span>
                      {tx.formName && tx.formName !== '-- Order --' && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {tx.formName}
                        </span>
                      )}
                    </div>
                    {tx.statusText && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {tx.statusText}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </OathModal>
  );
};

export default memo(OrderDetailModal);
